import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Shield, Lock, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * FinanceAccessDenied Component
 * 
 * Clean permission screen for users who manually navigate to /finance/* routes
 * without having finance access.
 */
export function FinanceAccessDenied() {
  return (
    <div 
      className="finance-access-denied"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        padding: 'var(--space-xl)'
      }}
      data-testid="finance-access-denied"
    >
      <div 
        style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'rgba(239, 68, 68, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 'var(--space-lg)'
        }}
      >
        <Lock size={36} style={{ color: 'var(--status-danger)' }} />
      </div>
      
      <h1 style={{ 
        fontSize: '1.5rem', 
        fontWeight: '600',
        marginBottom: 'var(--space-md)',
        color: 'var(--text-primary)'
      }}>
        Finance Access Restricted
      </h1>
      
      <p style={{ 
        color: 'var(--text-secondary)',
        maxWidth: '400px',
        marginBottom: 'var(--space-xl)',
        lineHeight: '1.6'
      }}>
        You don&apos;t have access to Finance features.
        <br />
        Please contact the account owner if you need access.
      </p>
      
      <a 
        href="/command-centre"
        className="btn btn-primary"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--space-sm)',
          textDecoration: 'none'
        }}
        data-testid="back-to-dashboard-btn"
      >
        <ArrowLeft size={16} />
        Back to Dashboard
      </a>
    </div>
  );
}

/**
 * FinanceRoute Component
 * 
 * Route wrapper that checks finance access before rendering children.
 * Redirects to clean access denied screen if unauthorized.
 */
export function FinanceRoute({ children }) {
  const { user, loading, hasFinanceAccess, isAuthenticated } = useAuth();
  const location = useLocation();

  // Still loading auth state
  if (loading) {
    return null; // Or a loading spinner
  }

  // Not authenticated - let ProtectedRoute handle redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check finance access
  if (!hasFinanceAccess()) {
    return <FinanceAccessDenied />;
  }

  // User has access - render children
  return children;
}

export default FinanceRoute;
